/**
 * Site-wide motion: headings revealed line by line, photographs drifting in
 * their frames, and smooth scrolling.
 *
 * The one deliberate exception to "behavior lives in the block". These three
 * are not any block's behavior — they are how the whole site moves, and a copy
 * in every block would be twenty copies to keep in step. A block opts in from
 * its own markup and styles and owns nothing here:
 *
 *   data-animate-text    on a heading: split into lines, each rising from
 *                        under a mask as the heading comes on screen.
 *   data-parallax-frame  on a photograph's frame: the frame gets
 *                        `data-parallax` and `--parallax` (1 entering at the
 *                        bottom, -1 leaving at the top); the block's own
 *                        stylesheet decides what, if anything, that moves.
 *
 * Nothing here runs for a visitor who asked for less motion, and with the
 * script blocked every heading and photograph simply stands where it is.
 */

import Lenis from 'lenis';

const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
const root = document.documentElement;

/* ------------------------------------------------------------------------ */
/* Headings, line by line                                                    */
/* ------------------------------------------------------------------------ */

const sources = new WeakMap();

/**
 * Split a heading into one masked span per rendered line.
 *
 * Only text and line breaks are split word by word; a heading holding any
 * other element is kept whole as a single line, so no inline markup is ever
 * taken apart. A `<br>` the stylesheet hides at this width is ignored, which
 * is what lets a headline that breaks on a wide screen run on on a phone.
 *
 * @param {HTMLElement} heading Element carrying `data-animate-text`.
 * @returns {void}
 */
const split = (heading) => {
    if (!sources.has(heading)) {
        sources.set(heading, heading.innerHTML);
    }

    heading.innerHTML = sources.get(heading);

    const nodes = [...heading.childNodes];
    const simple = nodes.every(
        (node) => Node.TEXT_NODE === node.nodeType || 'BR' === node.nodeName
    );

    const lines = [];

    if (!simple) {
        lines.push(heading.innerHTML);
    } else {
        // Words first, so the browser lays them out and tells us where each line starts.
        heading.textContent = '';

        for (const node of nodes) {
            if ('BR' === node.nodeName) {
                heading.append(node);
                continue;
            }

            for (const part of node.textContent.split(/(\s+)/)) {
                if (!part) {
                    continue;
                }

                if (/^\s+$/.test(part)) {
                    heading.append(document.createTextNode(' '));
                } else {
                    const word = document.createElement('span');
                    word.textContent = part;
                    word.style.display = 'inline-block';

                    // text-indent is inherited, and an inline-block starts a line
                    // of its own: without this every word of an indented heading
                    // carries the indent and the heading measures far too wide.
                    word.style.textIndent = '0';
                    heading.append(word);
                }
            }
        }

        let line = [];
        let top = null;

        for (const node of [...heading.childNodes]) {
            if ('BR' === node.nodeName) {
                if ('none' !== window.getComputedStyle(node).display && line.length) {
                    lines.push(line.join(' '));
                    line = [];
                    top = null;
                }
                continue;
            }

            if ('SPAN' !== node.nodeName) {
                continue;
            }

            if (null !== top && node.offsetTop > top + 2) {
                lines.push(line.join(' '));
                line = [];
            }

            top = node.offsetTop;
            line.push(node.textContent);
        }

        if (line.length) {
            lines.push(line.join(' '));
        }
    }

    heading.textContent = '';

    lines.forEach((content, index) => {
        const mask = document.createElement('span');
        const inner = document.createElement('span');

        mask.className = 'line-mask';
        inner.className = 'line';
        inner.style.setProperty('--line-index', String(index));

        if (simple) {
            inner.textContent = content;
        } else {
            inner.innerHTML = content;
        }

        mask.append(inner);

        // A space between lines: invisible between blocks, but it keeps the
        // last word of one line and the first of the next two words for a
        // screen reader, a search engine and anyone copying the text.
        if (index) {
            heading.append(document.createTextNode(' '));
        }

        heading.append(mask);
    });

    if ('in' !== heading.getAttribute('data-animate-text')) {
        heading.setAttribute('data-animate-text', 'ready');
    }
};

/**
 * Split every heading, reveal each as it comes on screen, re-split on resize.
 *
 * @returns {void}
 */
const headings = () => {
    const all = document.querySelectorAll('[data-animate-text]');

    if (!all.length) {
        return;
    }

    const watcher = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    entry.target.setAttribute('data-animate-text', 'in');
                    watcher.unobserve(entry.target);
                }
            }
        },
        { rootMargin: '0px 0px -10% 0px' }
    );

    /*
     * Not before the web fonts are in. Lines are measured, and measured in the
     * fallback face they break in different places — the split would then fix
     * those wrong breaks in place for good. The heading stays hidden meanwhile
     * (see main.scss), and the flag in <head> gives up after three seconds if
     * the fonts never come.
     */
    document.fonts.ready.then(() => {
        for (const heading of all) {
            split(heading);
            watcher.observe(heading);
        }
    });

    // Lines move when the width does; the height alone (a phone's toolbar) never moves them.
    let width = window.innerWidth;
    let timer = 0;

    window.addEventListener(
        'resize',
        () => {
            if (window.innerWidth === width) {
                return;
            }

            width = window.innerWidth;
            window.clearTimeout(timer);
            timer = window.setTimeout(() => all.forEach(split), 200);
        },
        { passive: true }
    );
};

/* ------------------------------------------------------------------------ */
/* One scroll loop                                                           */
/* ------------------------------------------------------------------------ */

/*
 * Everything that follows the scroll is updated from one place, in the same
 * frame the page moved in. With smooth scrolling that is Lenis's own `scroll`
 * event, which fires inside the frame Lenis moved the page in; a separate
 * `scroll` listener queuing its own frame would land one frame behind, and a
 * picture one frame behind the page it sits in is exactly what reads as a
 * judder. Without Lenis (the shop's forms, or before it has loaded) the
 * browser's own scroll drives the same loop, once a frame.
 *
 * Each update only measures, and hands back what it wants written. Every
 * measurement is taken before anything is written: a write between two reads
 * makes the browser lay the whole page out again to answer the second one,
 * and a few of those in one frame are what the first scroll after a load
 * stumbled on.
 */
const ticks = new Set();

/**
 * Run a batch of writes.
 *
 * @param {Array<Function|undefined>} writes Writes handed back by measurements.
 * @returns {void}
 */
const flush = (writes) => {
    for (const write of writes) {
        if (write) {
            write();
        }
    }
};

/**
 * Run every registered update now: all the reads, then all the writes.
 *
 * @returns {void}
 */
const tick = () => {
    const writes = [...ticks].map((update) => update());

    // For anything outside this file that also follows the scroll (the header
    // reading the tone of what it floats over): the same frame, not the next,
    // and while the layout is still the one just measured.
    window.dispatchEvent(new Event('theme:scroll'));

    flush(writes);
};

let nativeQueued = false;

/**
 * Coalesce native scroll and resize events into one tick per frame.
 *
 * @returns {void}
 */
const nativeTick = () => {
    if (!nativeQueued) {
        nativeQueued = true;
        window.requestAnimationFrame(() => {
            nativeQueued = false;
            tick();
        });
    }
};

window.addEventListener('scroll', nativeTick, { passive: true });
window.addEventListener('resize', nativeTick, { passive: true });

/* ------------------------------------------------------------------------ */
/* Heroes: how far the page has scrolled past them                           */
/* ------------------------------------------------------------------------ */

/**
 * Feed each hero marked `data-scroll-hero` two numbers for its stylesheet:
 * --scroll-past, the pixels scrolled past its top, and --scroll-progress, the
 * same as a share of its height (0 to 1). Measured only while it is on screen.
 *
 * @returns {void}
 */
const heroes = () => {
    const all = document.querySelectorAll('[data-scroll-hero]');

    if (!all.length) {
        return;
    }

    ticks.add(() => {
        const writes = [];

        for (const hero of all) {
            const { top, height } = hero.getBoundingClientRect();

            if (top > window.innerHeight || -top > height) {
                continue;
            }

            const past = Math.min(Math.max(-top, 0), height);

            writes.push(() => {
                hero.style.setProperty('--scroll-past', past.toFixed(1));
                hero.style.setProperty('--scroll-progress', (past / height).toFixed(4));
            });
        }

        return () => flush(writes);
    });
};

/* ------------------------------------------------------------------------ */
/* The footer, uncovered from underneath the page                            */
/* ------------------------------------------------------------------------ */

/**
 * Feed each element marked `data-scroll-reveal` --reveal-progress: 0 while it
 * is still below the fold, 1 once its foot has reached the bottom of the
 * screen. The footer uses it to rise from under the page more slowly than the
 * page moves.
 *
 * Measured from `offsetTop`, which ignores the element's own transform — the
 * position it is being moved away from, not the one it has been moved to.
 *
 * @returns {void}
 */
const reveals = () => {
    const all = document.querySelectorAll('[data-scroll-reveal]');

    if (!all.length) {
        return;
    }

    ticks.add(() => {
        const writes = [];

        for (const element of all) {
            const top = element.offsetTop - window.scrollY;
            const span = Math.min(element.offsetHeight, window.innerHeight);
            const progress =
                top > window.innerHeight ? 0 : Math.min(1, (window.innerHeight - top) / span);

            writes.push(() => element.style.setProperty('--reveal-progress', progress.toFixed(4)));
        }

        return () => flush(writes);
    });
};

/* ------------------------------------------------------------------------ */
/* Photographs drifting in their frames                                      */
/* ------------------------------------------------------------------------ */

/**
 * Feed each frame near the screen its position against the viewport.
 *
 * Frames are watched from a quarter of a screen away, so a photograph already
 * has its position before its first row of pixels shows — measured only once
 * it is visible, it would start from the middle and jump.
 *
 * @returns {void}
 */
const parallax = () => {
    const frames = document.querySelectorAll('[data-parallax-frame]');

    if (!frames.length) {
        return;
    }

    const near = new Set();

    /**
     * Measure a set of frames, then write each one's position.
     *
     * @param {Iterable<HTMLElement>} set Frames marked `data-parallax-frame`.
     * @returns {Function} The writes, to run once every read is done.
     */
    const measure = (set) => {
        const middle = window.innerHeight / 2;
        const offsets = [...set].map((frame) => {
            const { top, height } = frame.getBoundingClientRect();
            const offset = (top + height / 2 - middle) / (middle + height / 2);

            return [frame, Math.max(-1, Math.min(1, offset)).toFixed(4)];
        });

        return () => {
            for (const [frame, offset] of offsets) {
                frame.style.setProperty('--parallax', offset);
            }
        };
    };

    const watcher = new IntersectionObserver(
        (entries) => {
            const entering = [];

            for (const entry of entries) {
                if (entry.isIntersecting) {
                    near.add(entry.target);
                    entering.push(entry.target);
                } else {
                    near.delete(entry.target);
                }
            }

            measure(entering)();
        },
        { rootMargin: '25% 0px' }
    );

    // A block enlarges its photograph only where this is set, so a frame the
    // script never reaches keeps its crop exactly as it was. Every frame is
    // claimed first and measured after, in one pass — not claimed and measured
    // in turn, which laid the page out again for each of them.
    for (const frame of frames) {
        frame.setAttribute('data-parallax', '');
    }

    measure(frames)();

    for (const frame of frames) {
        watcher.observe(frame);
    }

    ticks.add(() => measure(near));
};

/* ------------------------------------------------------------------------ */
/* Smooth scrolling                                                          */
/* ------------------------------------------------------------------------ */

/**
 * Lenis, bundled with this file rather than fetched once it runs: a copy that
 * arrived a few hundred milliseconds late took over a scroll already under
 * way, and the page lurched as the visitor's own gesture changed hands. It is
 * small (about 5 kB gzipped), and it is only started when motion is wanted.
 *
 * Not on the cart, the checkout or the account pages: those are forms, where
 * the scroll should do exactly what the visitor's hand does. `autoToggle`
 * stops it while the menu holds the page still (`.has-menu-open` sets
 * overflow on <html>), and a dialog — the cart drawer, the search panel —
 * scrolls natively inside itself.
 *
 * Once it runs it drives the loop above from inside its own frame, and the
 * native listeners step aside so nothing is measured twice.
 *
 * @returns {void}
 */
const smoothScroll = () => {
    const body = document.body.classList;

    if (
        body.contains('woocommerce-cart') ||
        body.contains('woocommerce-checkout') ||
        body.contains('woocommerce-account')
    ) {
        return;
    }

    const lenis = new Lenis({
        autoRaf: true,
        autoToggle: true,
        allowNestedScroll: true,
        anchors: { offset: -96 },
        lerp: 0.085,
        prevent: (node) => null !== node.closest('dialog'),
    });

    window.removeEventListener('scroll', nativeTick);
    lenis.on('scroll', tick);
};

/* ------------------------------------------------------------------------ */

if (!motion.matches) {
    // First, so the page's very first scroll is already the smooth one.
    smoothScroll();
    headings();
    heroes();
    reveals();
    parallax();
    tick();
    root.classList.add('motion-live');
}
